import { cn } from '@/utils';
import { Spinner } from '@analytical-alley/ui';
import React, { useEffect, useState } from 'react';

export interface WithLoaderProps {
    isLoading: boolean;
    isFetching: boolean;
    className?: string;
    children: React.ReactNode;
    loadingMessage?: string | boolean;
    delayedMessage?: string | boolean;
    delayedMessageThreshold?: number;
}

export const WithLoader = ({
    children,
    isLoading,
    isFetching,
    className,
    loadingMessage = 'Loading...',
    delayedMessage = 'This is taking longer than usual. Please wait...',
    delayedMessageThreshold = 5000, // 5 seconds
}: WithLoaderProps) => {
    const [showDelayedMessage, setShowDelayedMessage] = useState(false);
    const [loadingStartTime, setLoadingStartTime] = useState<number | null>(
        null,
    );

    const isCurrentlyLoading = isLoading || isFetching;

    useEffect(() => {
        if (isCurrentlyLoading) {
            // Start tracking when loading begins
            if (loadingStartTime === null) {
                setLoadingStartTime(Date.now());
            }

            // Set up timer to show delayed message
            const timer = setTimeout(() => {
                setShowDelayedMessage(true);
            }, delayedMessageThreshold);

            return () => clearTimeout(timer);
        } else {
            // Reset when loading stops
            setShowDelayedMessage(false);
            setLoadingStartTime(null);
        }
    }, [isCurrentlyLoading, delayedMessageThreshold, loadingStartTime]);

    if (!isCurrentlyLoading) {
        return <div>{children}</div>;
    }

    return (
        <div
            className={cn(className, {
                'animate-pulse': isCurrentlyLoading,
            })}
        >
            {/* Loading overlay */}
            <div className="absolute rounded-2xl top-0 left-0 w-full h-full z-30 inset-0 bg-stone-900/25">
                <div className="text-center flex items-center flex-col justify-center h-full max-h-screen">
                    <Spinner size="xl" />
                    {loadingMessage && (
                        <p className="text-sm font-medium mb-2">
                            {loadingMessage}
                        </p>
                    )}
                    {showDelayedMessage && delayedMessage && (
                        <p className="text-xs max-w-md">{delayedMessage}</p>
                    )}
                </div>
            </div>

            {/* Content (dimmed while loading) */}
            <div className="pointer-events-none">{children}</div>
        </div>
    );
};
